import React, { useRef, useState, useEffect, useMemo } from "react";
import Globe from "react-globe.gl";
import { MapPinned, Play } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";

export function GlobeView({ members, onSelect, onRefresh }) {
  const wrapRef = useRef(null);
  const globeRef = useRef(null);
  const timerRef = useRef(null);
  const playingRef = useRef(false);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [step, setStep] = useState(-1); // -1 = idle (show all)
  const [caption, setCaption] = useState(null);
  const [geoPending, setGeoPending] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setDims({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { points, arcs } = useMemo(() => {
    const pts = [];
    const ars = [];
    members.forEach((m) => {
      const hasCur = typeof m.current_lat === "number" && typeof m.current_lng === "number";
      const hasOrg = typeof m.origin_lat === "number" && typeof m.origin_lng === "number";
      if (hasCur) pts.push({ id: m.id, name: m.full_name, kind: "current", place: m.current_place, lat: m.current_lat, lng: m.current_lng, color: "#082D4A" });
      if (hasOrg) pts.push({ id: m.id, name: m.full_name, kind: "origin", place: m.origin_place, lat: m.origin_lat, lng: m.origin_lng, color: "#D4A017" });
      if (hasCur && hasOrg) {
        ars.push({
          id: m.id, name: m.full_name,
          originPlace: m.origin_place, currentPlace: m.current_place,
          startLat: m.origin_lat, startLng: m.origin_lng,
          endLat: m.current_lat, endLng: m.current_lng,
        });
      }
    });
    return { points: pts, arcs: ars };
  }, [members]);

  useEffect(() => {
    if (!globeRef.current) return;
    const controls = globeRef.current.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.5;
    if (points.length) {
      const p = points[0];
      globeRef.current.pointOfView({ lat: p.lat, lng: p.lng, altitude: 2.2 }, 1000);
    }
  }, [points.length]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Poll background geocoding progress; refresh pins as places resolve.
  useEffect(() => {
    let active = true;
    let last = -1;
    const poll = async () => {
      try {
        const { pending } = await api.geocodeStatus();
        if (!active) return;
        setGeoPending(pending);
        if (pending !== last) {
          const decreased = last === -1 || pending < last;
          last = pending;
          if (decreased) onRefresh?.(); // refresh pins only as places resolve
        }
      } catch (e) { console.error("Geocode status poll failed", e); }
    };
    poll();
    const iv = setInterval(poll, 3000);
    return () => { active = false; clearInterval(iv); };
  }, [onRefresh]);

  const focusArc = (a) => {
    if (!globeRef.current) return;
    const midLat = (a.startLat + a.endLat) / 2;
    let dLng = a.endLng - a.startLng;
    if (Math.abs(dLng) > 180) dLng = dLng - Math.sign(dLng) * 360;
    const midLng = a.startLng + dLng / 2;
    globeRef.current.pointOfView({ lat: midLat, lng: midLng, altitude: 1.9 }, 1200);
    setCaption({ name: a.name, from: a.originPlace, to: a.currentPlace });
  };

  const playJourney = () => {
    if (!arcs.length || playingRef.current) return;
    playingRef.current = true;
    if (globeRef.current) globeRef.current.controls().autoRotate = false;
    let i = 0;
    setStep(0);
    focusArc(arcs[0]);
    const tick = () => {
      if (!playingRef.current) return;
      i += 1;
      if (i >= arcs.length) {
        playingRef.current = false;
        setStep(-1);
        setCaption(null);
        if (globeRef.current) globeRef.current.controls().autoRotate = true;
        return;
      }
      setStep(i);
      focusArc(arcs[i]);
      timerRef.current = setTimeout(tick, 3800);
    };
    timerRef.current = setTimeout(tick, 3800);
  };

  const stopJourney = () => {
    playingRef.current = false;
    clearTimeout(timerRef.current);
    setStep(-1);
    setCaption(null);
    if (globeRef.current) globeRef.current.controls().autoRotate = true;
  };

  const displayArcs = step === -1 ? arcs : arcs.slice(0, step + 1);
  const playing = step !== -1;

  return (
    <div ref={wrapRef} data-testid="globe-view" className="relative h-full w-full bg-[#0b1120]">
      {dims.w > 0 && (
        <Globe
          ref={globeRef}
          width={dims.w}
          height={dims.h}
          backgroundColor="#0b1120"
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
          bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
          pointsData={points}
          pointLat="lat"
          pointLng="lng"
          pointColor="color"
          pointAltitude={0.02}
          pointRadius={0.45}
          pointLabel={(d) => `<div style="font-family:'Plus Jakarta Sans',sans-serif;background:#fff;color:#082D4A;padding:6px 10px;border-radius:10px;box-shadow:0 4px 12px rgba(0,0,0,.25);font-size:12px"><b>${d.name}</b><br/>${d.kind === "origin" ? "From" : "Lives in"}: ${d.place || ""}</div>`}
          onPointClick={(p) => onSelect(p.id)}
          arcsData={displayArcs}
          arcStartLat="startLat"
          arcStartLng="startLng"
          arcEndLat="endLat"
          arcEndLng="endLng"
          arcColor={() => ["#D4A017", "#082D4A"]}
          arcDashLength={0.5}
          arcDashGap={0.2}
          arcDashAnimateTime={2000}
          arcStroke={0.6}
          atmosphereColor="#D4A017"
          atmosphereAltitude={0.18}
        />
      )}

      {/* Migration timeline control */}
      {arcs.length > 0 && (
        <div className="absolute left-1/2 top-4 z-20 -translate-x-1/2">
          <Button
            onClick={playing ? stopJourney : playJourney}
            data-testid="timeline-play-button"
            className="rounded-full bg-white/95 text-[#082D4A] shadow-lg hover:bg-white"
          >
            <Play size={16} className="mr-2 fill-[#082D4A]" />
            {playing ? `Stop journey (${step + 1}/${arcs.length})` : "Play migration journey"}
          </Button>
        </div>
      )}

      {caption && (
        <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2" data-testid="timeline-caption">
          <div className="rounded-2xl bg-white/95 px-5 py-3 text-center shadow-lg">
            <div className="font-serif text-lg font-semibold text-[#082D4A]">{caption.name}</div>
            <div className="text-sm text-[#64748B]">
              <span className="text-[#D4A017]">{caption.from}</span>
              <span className="mx-2">→</span>
              <span className="text-[#082D4A]">{caption.to}</span>
            </div>
          </div>
        </div>
      )}

      {/* Background geocoding progress */}
      {geoPending > 0 && (
        <div className="absolute left-1/2 top-16 z-20 -translate-x-1/2" data-testid="geocode-progress">
          <div className="flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm text-[#082D4A] shadow-lg">
            <span className="h-2 w-2 animate-pulse rounded-full bg-[#D4A017]" />
            Placing locations… {geoPending} remaining
          </div>
        </div>
      )}

      {points.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="max-w-sm rounded-2xl bg-white/95 p-6 text-center shadow-lg">
            <MapPinned className="mx-auto mb-2 text-[#D4A017]" size={28} />
            <p className="font-serif text-lg font-semibold text-[#082D4A]">No locations yet</p>
            <p className="mt-1 text-sm text-[#64748B]">
              Add a "From" or "Lives in" place to a person and they'll appear here on the globe.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
